import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@/store/auth';
import {
  isBareQuery,
  readRememberedFilters,
  writeRememberedFilters,
} from '@/utils/rememberedFilters';

/**
 * Q-01 -- list-page filter state that survives a page refresh, by mirroring
 * the page's own filters into the URL query string.
 *
 * Six things about this hook are deliberate and load-bearing:
 *
 * 1. THE INITIAL READ IS SYNCHRONOUS. Filters are restored in a lazy
 *    `useState` initializer, never in a `useEffect`. `CompactFilterBar`
 *    decides on its FIRST render whether to open its panel; if the restored
 *    filters only arrived on the second render, the list would come back
 *    filtered with the filter panel collapsed and no visible reason why.
 *    `initial` (note 4) is read in that SAME initializer, for the same reason.
 *
 * 2. WRITES USE `{ replace: true }`, following the shipped precedent in
 *    `ReportsPage` (it does the same for `?tab=` and its report filters).
 *    Consequence: the browser Back button LEAVES the page rather than undoing
 *    the last filter change. That is the accepted trade -- a filter bar with
 *    ten controls would otherwise bury the page the user came from under
 *    dozens of history entries. Nothing here gives back-button undo, so no
 *    release note may claim it. (Pinned by the "Back leaves the page" test.)
 *
 * 3. FILTERS ARE NAMESPACED BY `prefix`. A page like `PoliciesListPage` already
 *    reads ~ten UNPREFIXED params (`insurer`, `posp`, `customer`, `product`,
 *    `insurance_type`, `created_*`, `issue_date_*`, `lifecycle_status`) as
 *    ONE-SHOT deep-link scoping, and shows a "scoped" banner whenever any of
 *    them is present. If user-chosen filters were written back into those same
 *    names, that banner would go permanently live the moment anyone touched a
 *    dropdown. So a page that also accepts deep links MUST pass a `prefix`
 *    (the house spelling is `'f_'`, giving `?f_insurer=...`), keeping its own
 *    filter params in a separate namespace from the deep-link ones.
 *
 * 4. A DEEP-LINKED PAGE SEEDS ITSELF WITH `initial`, NOT WITH AN EFFECT. Note 3
 *    means the hook cannot see those unprefixed params, and note 1 means an
 *    adopter must not push them in from a `useEffect` (that lands on render 2,
 *    and the mirror-back would turn one-shot scoping permanent). `initial`
 *    closes the gap: it is read in the lazy initializer, merged UNDER the
 *    decoded prefixed params -- so a value the user actually chose (and that is
 *    in the URL under `prefix`) always beats the deep-link seed -- and it is
 *    NOT written to the query string on mount. The seeded values reach the URL
 *    only once the user touches a filter, i.e. once they are the user's own.
 *
 * 5. THE URL WRITE IS DEFERRED BY ONE COMMIT (the state is not). `setFilter`
 *    updates `filters` synchronously; the query string is rewritten in a
 *    layout effect, because that is the only point at which the base we edit
 *    is the query string react-router has actually committed -- including a
 *    param a CO-TENANT of the same page (`ReportsPage`'s `?tab=`) changed
 *    earlier in the same tick. A caller must therefore read `filters`, never
 *    `useSearchParams()`, immediately after calling a setter.
 *
 * 6. FILTERS ARE ALSO REMEMBERED FOR THE SESSION, so leaving the module and
 *    coming back through a bare sidebar link does not lose them (the URL alone
 *    only survives a refresh). Every user change is written to
 *    `sessionStorage` under user id + page (`rememberAs`, default the
 *    pathname). The remembered state is restored ONLY when the page opens with
 *    a completely empty query string, and it is restored in the SAME lazy
 *    initializer as note 1 -- never an effect. Any param at all -- a prefixed
 *    filter, an unprefixed deep-link seed -- means a link asked for something
 *    specific, and that wins and is left untouched. A restored state is then
 *    written to the URL like a user change (it IS the user's own). The
 *    `initial` seed is NOT remembered until the user touches a filter, for the
 *    reason in note 4; "Clear filters" (`clearAll`) forgets the page; logout
 *    forgets everything (`store/auth.ts`). See `utils/rememberedFilters.ts`.
 *
 * NOT IN SCOPE: the page number. See `UseFilterParamsOptions`.
 */

/** A `{ from, to }` date-range filter, stored as `<key>_from` / `<key>_to`. */
export interface FilterDateRange {
  from?: string;
  to?: string;
}

/** The three value shapes a filter may take. */
export type FilterParamValue = string | string[] | FilterDateRange;

/** The shape of a page's filter state: a flat record of the above. */
export type FilterParamsShape = Record<string, FilterParamValue>;

/**
 * A one-shot seed for the first render: either a partial filter object or a
 * function handed the CURRENT `URLSearchParams` (the whole query string, not
 * just this hook's namespace) so a page can decode its own deep-link params.
 */
export type FilterParamsSeed<T extends FilterParamsShape> =
  | Partial<T>
  | ((params: URLSearchParams) => Partial<T>);

export interface UseFilterParamsOptions<T extends FilterParamsShape> {
  /**
   * The page's empty filter state. It does double duty: it declares the KEYS
   * this hook owns (nothing else in the query string is ever touched) and,
   * per key, the VALUE SHAPE to decode into -- `[]` means multi-value,
   * `{}` (an object) means date range, anything else means a plain string.
   * It is also exactly what `clearAll()` resets to.
   *
   * Safe to pass a module-level constant: every value handed back is a COPY,
   * so a consumer mutating `filters.status` cannot corrupt it.
   */
  defaults: T;
  /**
   * Prepended verbatim to every param name this hook reads or writes. Default
   * `''` (no namespace). Pass `'f_'` on any page that also honours unprefixed
   * deep-link params -- see note 3 above.
   */
  prefix?: string;
  /**
   * Optional first-render seed for pages that also accept UNPREFIXED deep-link
   * params (`/policies?insurer=…&created_from=…`). Read once, in the lazy
   * initializer, and merged UNDER the URL's prefixed params -- see note 4.
   * Nothing is written to the query string on mount.
   *
   * ```ts
   * useFilterParams({
   *   defaults: EMPTY,
   *   prefix: 'f_',
   *   initial: (params) => ({ insurer: params.get('insurer') ?? '' }),
   * });
   * ```
   */
  initial?: FilterParamsSeed<T>;
  /**
   * The page's name in the session-remembered filter store (note 6). Defaults
   * to the current pathname, which is right for a list page; pass an explicit
   * name when one page is mounted under several paths, or `false` to opt out.
   * The `prefix` is appended, so two hooks on one page never share an entry.
   */
  rememberAs?: string | false;
}

export interface UseFilterParamsResult<T extends FilterParamsShape> {
  /** Current filter state. On the FIRST render this is already the URL's. */
  filters: T;
  /** Set one filter. A blank string / empty array / empty range removes it. */
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Merge several filters at once, or update from the previous state. */
  setFilters: (next: Partial<T> | ((prev: T) => Partial<T>)) => void;
  /** Reset to `defaults` and delete ONLY this hook's params from the URL. */
  clearAll: () => void;
  /** True when any owned filter differs from its default. Synchronous. */
  hasActiveFilters: boolean;
  /** How many owned keys are non-empty -- for a "Filters (2)" badge. */
  activeCount: number;
}

type Kind = 'string' | 'multi' | 'range';

function kindOf(value: FilterParamValue): Kind {
  if (Array.isArray(value)) return 'multi';
  if (value !== null && typeof value === 'object') return 'range';
  return 'string';
}

/**
 * Every value that leaves this module is a fresh array / object. The caller's
 * `defaults` is documented as a module-level constant, so handing back
 * `defaults.status` itself would let one page's `filters.status.push(x)`
 * corrupt that constant for every later mount -- including across `clearAll`,
 * which resets to the same object.
 */
function cloneValue(value: FilterParamValue): FilterParamValue {
  if (Array.isArray(value)) return [...value];
  if (value !== null && typeof value === 'object') return { ...(value as FilterDateRange) };
  return value;
}

function cloneShape<T extends FilterParamsShape>(source: T): T {
  const out = {} as Record<string, FilterParamValue>;
  for (const key of Object.keys(source)) out[key] = cloneValue(source[key]);
  return out as T;
}

function isEmptyValue(value: FilterParamValue): boolean {
  switch (kindOf(value)) {
    case 'multi':
      return (value as string[]).filter((v) => v !== '' && v != null).length === 0;
    case 'range': {
      const range = value as FilterDateRange;
      return !range.from && !range.to;
    }
    default:
      return !((value as string) ?? '');
  }
}

/**
 * Pure read: decode `params` into the shape `defaults` declares. Exported so a
 * page (or a test) can do the same restore outside a React render.
 *
 * `initial` is the fallback for a key the query string does not carry under
 * `prefix` -- the deep-link seed of note 4. Precedence, highest first:
 * prefixed URL param > `initial[key]` > `defaults[key]`.
 */
export function readFilterParams<T extends FilterParamsShape>(
  params: URLSearchParams,
  defaults: T,
  prefix = '',
  initial?: Partial<T>
): T {
  const out = {} as Record<string, FilterParamValue>;
  const fallback = (key: string): FilterParamValue => {
    const seeded = initial?.[key];
    return cloneValue(seeded !== undefined ? (seeded as FilterParamValue) : defaults[key]);
  };
  for (const key of Object.keys(defaults)) {
    const name = `${prefix}${key}`;
    switch (kindOf(defaults[key])) {
      case 'multi': {
        // Repeated params (`?f_status=a&f_status=b`) rather than a joined
        // string, so a value containing a comma round-trips intact.
        const values = params.getAll(name).filter((v) => v !== '');
        out[key] = values.length > 0 ? values : fallback(key);
        break;
      }
      case 'range': {
        const from = params.get(`${name}_from`) || '';
        const to = params.get(`${name}_to`) || '';
        const range: FilterDateRange = {};
        if (from) range.from = from;
        if (to) range.to = to;
        out[key] = from || to ? range : fallback(key);
        break;
      }
      default: {
        const value = params.get(name);
        out[key] = value !== null && value !== '' ? value : fallback(key);
      }
    }
  }
  return out as T;
}

/**
 * Pure write: a NEW `URLSearchParams` carrying `filters` under `prefix`, with
 * every param this hook does not own (deep-link scoping, `?tab=`, anything
 * else) preserved untouched. Empty values are DELETED, never written as
 * `key=`.
 */
export function writeFilterParams<T extends FilterParamsShape>(
  params: URLSearchParams,
  filters: T,
  defaults: T,
  prefix = ''
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.keys(defaults)) {
    const name = `${prefix}${key}`;
    const value = filters[key];
    switch (kindOf(defaults[key])) {
      case 'multi': {
        next.delete(name);
        if (!isEmptyValue(value)) {
          for (const item of (value as string[]).filter((v) => v !== '' && v != null)) {
            next.append(name, item);
          }
        }
        break;
      }
      case 'range': {
        const range = (value ?? {}) as FilterDateRange;
        if (range.from) next.set(`${name}_from`, range.from);
        else next.delete(`${name}_from`);
        if (range.to) next.set(`${name}_to`, range.to);
        else next.delete(`${name}_to`);
        break;
      }
      default: {
        if (!isEmptyValue(value)) next.set(name, value as string);
        else next.delete(name);
      }
    }
  }
  return next;
}

/**
 * Mirror a list page's filter state into the query string so it survives a
 * refresh, a bookmark and a shared link.
 *
 * DEBOUNCED SEARCH: this hook does no debouncing of its own. Feed it the
 * ALREADY-DEBOUNCED value (`useDebouncedSearch().debouncedTerm`), so typing
 * costs one URL write per pause, not one per keystroke.
 *
 * ```ts
 * const EMPTY = { search: '', insurer: '', status: [] as string[], issue_date: {} as FilterDateRange };
 * const { filters, setFilter, clearAll } = useFilterParams({ defaults: EMPTY, prefix: 'f_' });
 * ```
 */
export function useFilterParams<T extends FilterParamsShape>(
  options: UseFilterParamsOptions<T>
): UseFilterParamsResult<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  // Anything but a real id (no one signed in) disables remembering outright.
  const rawUserId: unknown = useAuthStore((s) => s.user?.id ?? null);
  const userId = typeof rawUserId === 'string' && rawUserId ? rawUserId : null;

  // `defaults` and `prefix` are read once. A page that re-declares `defaults`
  // inline every render must not cause a re-decode (or a re-render loop), and
  // a filter namespace that changed mid-life would orphan params in the URL.
  // The remembered-filter page name is fixed at mount for the same reason.
  const configRef = useRef<{ defaults: T; prefix: string; rememberAs: string | null }>({
    defaults: options.defaults,
    prefix: options.prefix ?? '',
    rememberAs:
      options.rememberAs === false ? null : `${options.rememberAs ?? pathname}#${options.prefix ?? ''}`,
  });
  const { defaults, prefix, rememberAs } = configRef.current;
  // Read at write time, not mount time: a remembered entry must only ever be
  // filed under the user who is signed in when it is written.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const remember = useCallback(
    (nextFilters: T) => {
      if (rememberAs === null) return;
      const query = writeFilterParams(new URLSearchParams(), nextFilters, defaults, prefix).toString();
      writeRememberedFilters(userIdRef.current, rememberAs, query);
    },
    [defaults, prefix, rememberAs]
  );

  // The filters whose URL write has not landed yet, or `null` when the query
  // string is already in step. See the layout effect below. Declared here
  // because the lazy initializer below may already need to queue one.
  const pendingRef = useRef<T | null>(null);

  // REQUIREMENT 1: lazy initializer -- the very first render already has the
  // restored filters, deep-link seed included. Do not move this into an effect.
  const [filters, setFiltersState] = useState<T>(() => {
    // Note 6: a BARE query string (the sidebar case) falls back to what this
    // user last chose on this page this session. Any param at all is a link
    // asking for something specific and wins -- including over an empty
    // remembered state, and without touching it.
    if (rememberAs !== null && isBareQuery(searchParams)) {
      const remembered = readRememberedFilters(userId, rememberAs);
      if (remembered !== null) {
        const restored = readFilterParams(new URLSearchParams(remembered), defaults, prefix);
        // Mirrored into the URL by the layout effect, exactly like a user
        // change, so a refresh or a copied link carries what is on screen.
        pendingRef.current = restored;
        return restored;
      }
    }
    const seed =
      typeof options.initial === 'function' ? options.initial(searchParams) : options.initial;
    return readFilterParams(searchParams, defaults, prefix, seed);
  });

  // A page opened from a link or a bookmark that carries this hook's OWN
  // prefixed params (not the `initial` seed -- note 4) remembers them, so a
  // fresh tab's away-and-back trip keeps them too. Nothing is forgotten here:
  // a link that carries none of our params leaves the remembered state alone.
  useEffect(() => {
    if (rememberAs === null) return;
    const own = writeFilterParams(
      new URLSearchParams(),
      readFilterParams(searchParams, defaults, prefix),
      defaults,
      prefix
    ).toString();
    if (own) writeRememberedFilters(userIdRef.current, rememberAs, own);
    // Mount only: later changes are remembered by `commit`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kept in a ref so the callbacks below are stable AND never lose either of
  // two setters that fire in one tick.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const commit = useCallback((nextFilters: T) => {
    filtersRef.current = nextFilters;
    setFiltersState(nextFilters);
    // Note 6. An all-empty state (Clear) FORGETS the page rather than
    // remembering "nothing", so the next bare visit starts clean.
    remember(nextFilters);
    // The URL write is NOT done here. `setSearchParams` -- functional form
    // included -- edits a base that react-router memoized at the last render
    // (`useSearchParams` closes over `useMemo(..., [location.search])`), so a
    // write issued from inside an event handler cannot see a param a CO-TENANT
    // changed earlier in that same tick. `ReportsPage` is exactly that: it
    // writes `?tab=` through its own `setSearchParams` next to these filters,
    // and a write from here would silently revert the tab. Deferring to a
    // layout effect means we edit the query string react-router has actually
    // committed, whichever of the two writers went first.
    pendingRef.current = nextFilters;
  }, [remember]);

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    if (pending === null) return;
    pendingRef.current = null;
    // `searchParams` here is the COMMITTED query string, co-tenant edits and
    // all; the functional updater keeps that true even if another write is
    // queued behind this one.
    if (writeFilterParams(searchParams, pending, defaults, prefix).toString() === searchParams.toString()) {
      return; // Nothing of ours changed in the URL -- do not push a navigation.
    }
    setSearchParams(
      (current) => writeFilterParams(current, pending, defaults, prefix),
      // Precedent: ReportsPage. Back leaves the page; it does not undo a filter.
      { replace: true }
    );
    // `filters` is a dependency because a filter change that leaves
    // `searchParams` untouched must still trigger this effect.
  }, [filters, searchParams, defaults, prefix, setSearchParams]);

  const setFilters = useCallback(
    (next: Partial<T> | ((prev: T) => Partial<T>)) => {
      const prev = filtersRef.current;
      const patch = typeof next === 'function' ? next(prev) : next;
      commit({ ...prev, ...patch });
    },
    [commit]
  );

  const setFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setFilters({ [key]: value } as unknown as Partial<T>);
    },
    [setFilters]
  );

  const clearAll = useCallback(() => {
    // A COPY of `defaults`, never `defaults` itself -- see `cloneValue`.
    commit(cloneShape(defaults));
  }, [commit, defaults]);

  const activeCount = useMemo(
    () => Object.keys(defaults).filter((key) => !isEmptyValue(filters[key])).length,
    [defaults, filters]
  );

  return {
    filters,
    setFilter,
    setFilters,
    clearAll,
    hasActiveFilters: activeCount > 0,
    activeCount,
  };
}
